import { bech32, utf8 } from "@scure/base";
/** Checks if a value is null or undefined */
export const isNil = (x) => [null, undefined].includes(x);
/**
 * Executes a function if the value is defined
 * @param x - The value to check
 * @param f - Function to execute if x is defined
 * @returns Result of f(x) if x is defined, undefined otherwise
 */
export const ifLet = (x, f) => x === undefined ? undefined : f(x);
/** Function that does nothing and returns undefined */
export const noop = (...args) => undefined;
/**
 * Returns the first element of an array
 * @param xs - The array
 * @returns First element or undefined
 */
export const first = (xs, ...args) => xs[0];
/**
 * Returns the first element of the first array in a nested array
 * @param xs - Array of arrays
 * @returns First element of first array or undefined
 */
export const ffirst = (xs, ...args) => xs[0][0];
/**
 * Returns the last element of an array
 * @param xs - The array
 * @returns Last element or undefined
 */
export const last = (xs, ...args) => xs[xs.length - 1];
/**
 * Returns the input value unchanged
 * @param x - Any value
 * @returns The same value
 */
export const identity = (x, ...args) => x;
/**
 * Creates a function that always returns the same value
 * @param x - Value to return
 * @returns Function that returns x
 */
export const always = (x, ...args) => () => x;
/**
 * Returns the logical NOT of a value
 * @param x - Value to negate
 * @returns !x
 */
export const not = (x, ...args) => !x;
/** Returns a function that returns the boolean negation of the given function */
export const complement = (f) => (...args) => !f(...args);
/** Converts a `Maybe<number>` to a number, defaulting to 0 */
export const num = (x) => x || 0;
/** Adds two numbers, handling undefined values */
export const add = (x, y) => num(x) + num(y);
/** Subtracts two numbers, handling undefined values */
export const sub = (x, y) => num(x) - num(y);
/** Multiplies two numbers, handling undefined values */
export const mul = (x, y) => num(x) * num(y);
/** Divides two numbers, handling undefined values */
export const div = (x, y) => num(x) / y;
/** Increments a number by 1, handling undefined values */
export const inc = (x) => add(x, 1);
/** Decrements a number by 1, handling undefined values */
export const dec = (x) => sub(x, 1);
/** Less than comparison, handling undefined values */
export const lt = (x, y) => num(x) < num(y);
/** Less than or equal comparison, handling undefined values */
export const lte = (x, y) => num(x) <= num(y);
/** Greater than comparison, handling undefined values */
export const gt = (x, y) => num(x) > num(y);
/** Greater than or equal comparison, handling undefined values */
export const gte = (x, y) => num(x) >= num(y);
/** Returns maximum value in array, handling undefined values */
export const max = (xs) => xs.reduce((a, b) => Math.max(num(a), num(b)), 0);
/** Returns minimum value in array, handling undefined values */
export const min = (xs) => {
    const [head, ...tail] = xs.filter(x => !isNil(x));
    if (tail.length === 0)
        return head || 0;
    return tail.reduce((a, b) => Math.min(a, b), head);
};
/** Returns sum of array values, handling undefined values */
export const sum = (xs) => xs.reduce((a, b) => add(a, b), 0);
/** Returns average of array values, handling undefined values */
export const avg = (xs) => sum(xs) / xs.length;
/**
 * Returns array with first n elements removed
 * @param n - Number of elements to drop
 * @param xs - Input array
 * @returns Array with first n elements removed
 */
export const drop = (n, xs) => xs.slice(n);
/**
 * Returns first n elements of array
 * @param n - Number of elements to take
 * @param xs - Input array
 * @returns Array of first n elements
 */
export const take = (n, xs) => xs.slice(0, n);
/**
 * Creates new object with specified keys removed
 * @param ks - Keys to remove
 * @param x - Source object
 * @returns New object without specified keys
 */
export const omit = (ks, x) => {
    const r = { ...x };
    for (const k of ks) {
        delete r[k];
    }
    return r;
};
/**
 * Creates new object excluding entries with specified values
 * @param xs - Values to exclude
 * @param x - Source object
 * @returns New object without entries containing specified values
 */
export const omitVals = (xs, x) => {
    const r = {};
    for (const [k, v] of Object.entries(x)) {
        if (!xs.includes(v)) {
            r[k] = v;
        }
    }
    return r;
};
/**
 * Creates new object with only specified keys
 * @param ks - Keys to keep
 * @param x - Source object
 * @returns New object with only specified keys
 */
export const pick = (ks, x) => {
    const r = { ...x };
    for (const k of Object.keys(x)) {
        if (!ks.includes(k)) {
            delete r[k];
        }
    }
    return r;
};
/**
 * Generates sequence of numbers from a to b
 * @param a - Start number (inclusive)
 * @param b - End number (exclusive)
 * @param step - Increment between numbers
 * @yields Numbers in sequence
 */
export function* range(a, b, step = 1) {
    for (let i = a; i < b; i += step) {
        yield i;
    }
}
/**
 * Yields indexed items
 * @param items - A collection of items
 * @yields tuples of [index, item]
 */
export function* enumerate(items) {
    for (let i = 0; i < items.length; i += 1) {
        yield [i, items[i]];
    }
}
/**
 * Creates new object with transformed keys
 * @param f - Function to transform keys
 * @param x - Source object
 * @returns Object with transformed keys
 */
export const mapKeys = (f, x) => {
    const r = {};
    for (const [k, v] of Object.entries(x)) {
        r[f(k)] = v;
    }
    return r;
};
/**
 * Creates new object with transformed values
 * @param f - Function to transform values
 * @param x - Source object
 * @returns Object with transformed values
 */
export const mapVals = (f, x) => {
    const r = {};
    for (const [k, v] of Object.entries(x)) {
        r[k] = f(v);
    }
    return r;
};
/**
 * Merges two objects, with left object taking precedence
 * @param a - Left object
 * @param b - Right object
 * @returns Merged object with a"s properties overriding b"s
 */
export const mergeLeft = (a, b) => ({
    ...b,
    ...a,
});
/**
 * Merges two objects, with right object taking precedence
 * @param a - Left object
 * @param b - Right object
 * @returns Merged object with b"s properties overriding a"s
 */
export const mergeRight = (a, b) => ({
    ...a,
    ...b,
});
/** Deep merge two objects, prioritizing the first argument. */
export const deepMergeLeft = (a, b) => deepMergeRight(b, a);
/** Deep merge two objects, prioritizing the second argument. */
export const deepMergeRight = (a, b) => {
    a = { ...a };
    for (const [k, v] of Object.entries(b)) {
        if (isPojo(v) && isPojo(a[k])) {
            a[k] = deepMergeRight(a[k], v);
        }
        else {
            a[k] = v;
        }
    }
    return a;
};
/**
 * Checks if a number is between two values (exclusive)
 * @param bounds - Lower and upper bounds
 * @param n - Number to check
 * @returns True if n is between low and high
 */
export const between = ([low, high], n) => n > low && n < high;
/**
 * Checks if a number is between two values (inclusive)
 * @param bounds - Lower and upper bounds
 * @param n - Number to check
 * @returns True if n is between low and high
 */
export const within = ([low, high], n) => n >= low && n <= high;
/**
 * Generates random integer between min and max (inclusive)
 * @param min - Minimum value
 * @param max - Maximum value
 * @returns Random integer
 */
export const randomInt = (min = 0, max = 9) => min + Math.round(Math.random() * (max - min));
/**
 * Generates random string ID
 * @returns Random string suitable for use as an ID
 */
export const randomId = () => Math.random().toString().slice(2);
/**
 * Removes protocol (http://, https://, etc) from URL
 * @param url - URL to process
 * @returns URL without protocol
 */
export const stripProtocol = (url) => url.replace(/.*:\/\//, "");
/**
 * Formats URL for display by removing protocol, www, and trailing slash
 * @param url - URL to format
 * @returns Formatted URL
 */
export const displayUrl = (url) => stripProtocol(url)
    .replace(/^(www\.)?/i, "")
    .replace(/\/$/, "");
/**
 * Extracts and formats domain from URL
 * @param url - URL to process
 * @returns Formatted domain name
 */
export const displayDomain = (url) => displayUrl(first(url.split(/[\/\?]/)));
/**
 * Creates a promise that resolves after specified time
 * @param t - Time in milliseconds
 * @returns Promise that resolves after t milliseconds
 */
export const sleep = (t) => new Promise(resolve => setTimeout(resolve, t));
/**
 * Concatenates multiple arrays, filtering out null/undefined
 * @param xs - Arrays to concatenate
 * @returns Combined array
 */
export const concat = (...xs) => xs.flatMap(x => (isNil(x) ? [] : x));
/**
 * Appends element to array
 * @param x - Element to append
 * @param xs - Array to append to
 * @returns New array with element appended
 */
export const append = (x, xs) => concat(xs, [x]);
/**
 * Creates union of two arrays
 * @param a - First array
 * @param b - Second array
 * @returns Array containing unique elements from both arrays
 */
export const union = (a, b) => uniq([...a, ...b]);
/**
 * Returns elements common to both arrays
 * @param a - First array
 * @param b - Second array
 * @returns Array of elements present in both inputs
 */
export const intersection = (a, b) => {
    const s = new Set(b);
    return a.filter(x => s.has(x));
};
/**
 * Returns elements in first array not present in second
 * @param a - Source array
 * @param b - Array of elements to exclude
 * @returns Array containing elements unique to first array
 */
export const difference = (a, b) => {
    const s = new Set(b);
    return a.filter(x => !s.has(x));
};
/**
 * Removes all instances of an element from array
 * @param a - Element to remove
 * @param xs - Source array
 * @returns New array with element removed
 */
export const remove = (a, xs) => xs.filter(x => x !== a);
/**
 * Returns elements from second array not present in first
 * @param a - Array of elements to exclude
 * @param b - Source array
 * @returns Filtered array
 */
export const without = (a, b) => b.filter(x => !a.includes(x));
/**
 * Toggles presence of element in array
 * @param x - Element to toggle
 * @param xs - Source array
 * @returns New array with element added or removed
 */
export const toggle = (x, xs) => (xs.includes(x) ? remove(x, xs) : append(x, xs));
/**
 * Constrains number between min and max values
 * @param bounds - Minimum and maximum allowed values
 * @param n - Number to clamp
 * @returns Clamped value
 */
export const clamp = ([min, max], n) => Math.min(max, Math.max(min, n));
/**
 * Round a number to the nearest float precision
 * @param precision - Number of decimal places
 * @param x - Number to round
 * @returns Formatted number
 */
export const round = (precision, x) => Math.round(x * Math.pow(10, precision)) / Math.pow(10, precision);
/**
 * Safely parses JSON string
 * @param json - JSON string to parse
 * @returns Parsed object or null if invalid
 */
export const parseJson = (json) => {
    if (!json)
        return undefined;
    try {
        return JSON.parse(json);
    }
    catch (e) {
        return undefined;
    }
};
/**
 * Gets and parses JSON from localStorage
 * @param k - Storage key
 * @returns Parsed value or undefined if invalid/missing
 */
export const getJson = (k) => parseJson(localStorage.getItem(k) || "");
/**
 * Stringifies and stores value in localStorage
 * @param k - Storage key
 * @param v - Value to store
 */
export const setJson = (k, v) => localStorage.setItem(k, JSON.stringify(v));
/**
 * Safely executes function and handles errors
 * @param f - Function to execute
 * @param onError - Optional error handler
 * @returns Function result or undefined if error
 */
export const tryCatch = (f, onError) => {
    try {
        const r = f();
        if (r instanceof Promise) {
            r.catch(e => onError?.(e));
        }
        return r;
    }
    catch (e) {
        onError?.(e);
    }
    return undefined;
};
/**
 * Truncates string to length, breaking at word boundaries
 * @param s - String to truncate
 * @param l - Maximum length
 * @param suffix - String to append if truncated
 * @returns Truncated string
 */
export const ellipsize = (s, l, suffix = "...") => {
    if (s.length < l * 1.1) {
        return s;
    }
    while (s.length > l && s.includes(" ")) {
        s = s.split(" ").slice(0, -1).join(" ");
    }
    return s + suffix;
};
/**
 * Checks if value is a plain object
 * @param obj - Value to check
 * @returns True if value is a plain object
 */
export const isPojo = (obj) => {
    if (obj === null || typeof obj !== "object") {
        return false;
    }
    return Object.getPrototypeOf(obj) === Object.prototype;
};
/**
 * Deep equality comparison
 * @param a - First value
 * @param b - Second value
 * @returns True if values are deeply equal
 */
export const equals = (a, b) => {
    if (a === b)
        return true;
    if (a instanceof Set && b instanceof Set) {
        a = Array.from(a);
        b = Array.from(b);
    }
    if (a instanceof Set) {
        if (!(b instanceof Set) || a.size !== b.size) {
            return false;
        }
        return Array.from(a).every(x => b.has(x));
    }
    if (Array.isArray(a)) {
        if (!Array.isArray(b) || a.length !== b.length) {
            return false;
        }
        for (let i = 0; i < a.length; i++) {
            if (!equals(a[i], b[i])) {
                return false;
            }
        }
        return true;
    }
    if (isPojo(a)) {
        if (!isPojo(b)) {
            return false;
        }
        const aKeys = Object.keys(a);
        const bKeys = Object.keys(b);
        if (aKeys.length !== bKeys.length) {
            return false;
        }
        for (const k of aKeys) {
            if (!equals(a[k], b[k])) {
                return false;
            }
        }
        return true;
    }
    return false;
};
// Curried utils
/** Returns a function that gets the nth element of an array */
export const nth = (i) => (xs, ...args) => xs[i];
/** Returns a function that checks if nth element equals value */
export const nthEq = (i, v) => (xs, ...args) => xs[i] === v;
/** Returns a function that checks if nth element does not equal value */
export const nthNe = (i, v) => (xs, ...args) => xs[i] !== v;
/** Returns a function that checks if key/value pairs of x match all pairs in spec */
export const spec = (values) => (x, ...args) => {
    if (Array.isArray(values)) {
        for (let i = 0; i < values.length; i++) {
            if (x[i] !== values[i]) {
                return false;
            }
        }
    }
    else {
        for (const [k, v] of Object.entries(values)) {
            if (x[k] !== v)
                return false;
        }
    }
    return true;
};
/** Returns a function that checks equality with value */
export const eq = (v) => (x) => x === v;
/** Returns a function that checks inequality with value */
export const ne = (v) => (x) => x !== v;
/** Returns a function that gets property value from object */
export const prop = (k) => (x) => x[k];
/** Returns a function that adds/updates a property on object */
export const assoc = (k, v) => (o) => ({ ...o, [k]: v });
/** Returns a function that removes a property on object */
export const dissoc = (k) => (o) => omit([k], o);
/** Generates a hash string from input string */
export const hash = (s) => Math.abs(s.split("").reduce((a, b) => ((a << 5) - a + b.charCodeAt(0)) | 0, 0)).toString();
// Collections
/** Splits array into two parts at index */
export const splitAt = (n, xs) => [xs.slice(0, n), xs.slice(n)];
/** Inserts element into array at index */
export const insert = (n, x, xs) => [...xs.slice(0, n), x, ...xs.slice(n)];
/** Returns random element from array */
export const choice = (xs) => xs[Math.floor(xs.length * Math.random())];
/** Returns shuffled copy of iterable */
export const shuffle = (xs) => Array.from(xs).sort(() => (Math.random() > 0.5 ? 1 : -1));
/** Returns n random elements from array */
export const sample = (n, xs) => shuffle(xs).slice(0, n);
/** Checks if value is iterable */
export const isIterable = (x) => Symbol.iterator in Object(x);
/** Ensures value is iterable by wrapping in array if needed */
export const toIterable = (x) => (isIterable(x) ? x : [x]);
/** Ensures value is array by wrapping if needed */
export const ensurePlural = (x) => (x instanceof Array ? x : [x]);
/** Converts string or number to number */
export const ensureNumber = (x) => parseFloat(x);
/** Returns a function that gets property value from object */
export const pluck = (k, xs) => xs.map(x => x[k]);
/**
 * Creates object from array of key-value pairs
 * @param pairs - Array of [key, value] tuples
 * @returns Object with keys and values from pairs
 */
export const fromPairs = (pairs) => {
    const r = {};
    for (const [k, v] of pairs) {
        if (k && v) {
            r[k] = v;
        }
    }
    return r;
};
/**
 * Filters object values based on predicate
 * @param f - Function to test values
 * @param x - Object to filter
 * @returns Object with only values that pass predicate
 */
export const filterVals = (f, x) => {
    const r = {};
    for (const k in x) {
        if (f(x[k])) {
            r[k] = x[k];
        }
    }
    return r;
};
/**
 * Flattens array of arrays into single array
 * @param xs - Array of arrays to flatten
 * @returns Flattened array
 */
export const flatten = (xs) => xs.flatMap(identity);
/**
 * Splits array into two arrays based on predicate
 * @param f - Function to test elements
 * @param xs - Array to partition
 * @returns Tuple of [matching, non-matching] arrays
 */
export const partition = (f, xs) => {
    const a = [];
    const b = [];
    for (const x of xs) {
        if (f(x)) {
            a.push(x);
        }
        else {
            b.push(x);
        }
    }
    return [a, b];
};
/**
 * Returns array with duplicate elements removed
 * @param xs - Array with possible duplicates
 * @returns Array with unique elements
 */
export const uniq = (xs) => Array.from(new Set(xs));
/**
 * Returns array with elements unique by key function
 * @param f - Function to generate key for each element
 * @param xs - Input array
 * @returns Array with elements unique by key
 */
export const uniqBy = (f, xs) => {
    const s = new Set();
    const r = [];
    for (const x of xs) {
        const k = f(x);
        if (s.has(k)) {
            continue;
        }
        s.add(k);
        r.push(x);
    }
    return r;
};
/**
 * Returns sorted copy of array
 * @param xs - Array to sort
 * @returns New sorted array
 */
export const sort = (xs) => [...xs].sort();
/**
 * Returns array sorted by key function
 * @param f - Function to generate sort key
 * @param xs - Array to sort
 * @returns Sorted array
 */
export const sortBy = (f, xs) => [...xs].sort((a, b) => {
    const x = f(a);
    const y = f(b);
    return x < y ? -1 : x > y ? 1 : 0;
});
/**
 * Groups array elements by key function
 * @param f - Function to generate group key
 * @param xs - Array to group
 * @returns Map of groups
 */
export const groupBy = (f, xs) => {
    const r = new Map();
    for (const x of xs) {
        const k = f(x);
        let v = r.get(k);
        if (!v) {
            v = [];
            r.set(k, v);
        }
        v.push(x);
    }
    return r;
};
/**
 * Creates map from array using key function
 * @param f - Function to generate key
 * @param xs - Array to index
 * @returns Map of values by key
 */
export const indexBy = (f, xs) => {
    const r = new Map();
    for (const x of xs) {
        r.set(f(x), x);
    }
    return r;
};
/**
 * Creates array of specified length using generator function
 * @param n - Length of array
 * @param f - Function to generate each element
 * @returns Generated array
 */
export const initArray = (n, f) => {
    const result = [];
    for (let i = 0; i < n; i++) {
        result.push(f());
    }
    return result;
};
/**
 * Splits array into chunks of specified length
 * @param chunkLength - Maximum length of each chunk
 * @param xs - Array to split
 * @returns Array of chunks
 */
export const chunk = (chunkLength, xs) => {
    const result = [];
    const current = [];
    for (const item of xs) {
        if (current.length < chunkLength) {
            current.push(item);
        }
        else {
            result.push(current.splice(0));
            current.push(item);
        }
    }
    if (current.length > 0) {
        result.push(current);
    }
    return result;
};
/**
 * Splits array into specified number of chunks
 * @param n - Number of chunks
 * @param xs - Array to split
 * @returns Array of n chunks
 */
export const chunks = (n, xs) => {
    const result = initArray(n, () => []);
    for (let i = 0; i < xs.length; i++) {
        result[i % n].push(xs[i]);
    }
    return result;
};
/**
 * Creates function that only executes once
 * @param f - Function to wrap
 * @returns Function that executes f only on first call
 */
export const once = (f) => {
    let called = false;
    return (...args) => {
        if (!called) {
            called = true;
            f(...args);
        }
    };
};
/**
 * Memoizes function results based on arguments
 * @param f - Function to memoize
 * @returns Memoized function
 */
export const memoize = (f) => {
    let prevArgs;
    let result;
    return (...args) => {
        if (!equals(prevArgs, args)) {
            prevArgs = args;
            result = f(...args);
        }
        return result;
    };
};
/**
 * Creates throttled version of function
 * @param ms - Minimum time between calls
 * @param f - Function to throttle
 * @returns Throttled function
 */
export const throttle = (ms, f) => {
    if (ms === 0) {
        return f;
    }
    let paused = false;
    let nextArgs;
    const unpause = () => {
        if (nextArgs) {
            f(...nextArgs);
            nextArgs = undefined;
        }
        paused = false;
    };
    return (...thisArgs) => {
        if (!paused) {
            f(...thisArgs);
            paused = true;
            setTimeout(unpause, ms);
        }
        else {
            nextArgs = thisArgs;
        }
    };
};
/**
 * Creates throttled function that returns cached value
 * @param ms - Minimum time between updates
 * @param f - Function to throttle
 * @returns Function returning latest value
 */
export const throttleWithValue = (ms, f) => {
    let value;
    const update = throttle(ms, () => {
        value = f();
    });
    return () => {
        update();
        return value;
    };
};
/**
 * Creates batching function that collects items
 * this function does not delay execution, if a series of items is passed in sequence
 * the first item will be processed immediately, and the rest will be batched
 * @param t - Time window for batching
 * @param f - Function to process batch
 * @returns Function that adds items to batch
 */
export const batch = (t, f) => {
    const xs = [];
    const cb = throttle(t, () => xs.length > 0 && f(xs.splice(0)));
    return (x) => {
        xs.push(x);
        cb();
    };
};
/**
 * Creates batching function that returns results
 * @param t - Time window for batching
 * @param execute - Function to process batch
 * @returns Function that returns promise of result
 */
export const batcher = (t, execute) => {
    const queue = [];
    const _execute = async () => {
        const items = queue.splice(0);
        const results = await execute(items.map(item => item.request));
        results.forEach(async (r, i) => {
            if (results.length === items.length) {
                items[i].resolve(await r);
            }
            else {
                items[i].reject("Execute must return a result for each request");
            }
        });
    };
    return (request) => new Promise((resolve, reject) => {
        if (queue.length === 0) {
            setTimeout(_execute, t);
        }
        queue.push({ request, resolve, reject });
    });
};
/**
 * Adds value to Set at key in object
 * @param m - Object mapping keys to Sets
 * @param k - Key to add to
 * @param v - Value to add
 */
export const addToKey = (m, k, v) => {
    const s = m[k] || new Set();
    s.add(v);
    m[k] = s;
};
/**
 * Pushes value to array at key in object
 * @param m - Object mapping keys to arrays
 * @param k - Key to push to
 * @param v - Value to push
 */
export const pushToKey = (m, k, v) => {
    const a = m[k] || [];
    a.push(v);
    m[k] = a;
};
/**
 * Adds value to Set at key in Map
 * @param m - Map of Sets
 * @param k - Key to add to
 * @param v - Value to add
 */
export const addToMapKey = (m, k, v) => {
    const s = m.get(k) || new Set();
    s.add(v);
    m.set(k, s);
};
/**
 * Pushes value to array at key in Map
 * @param m - Map of arrays
 * @param k - Key to push to
 * @param v - Value to push
 */
export const pushToMapKey = (m, k, v) => {
    const a = m.get(k) || [];
    a.push(v);
    m.set(k, a);
};
/**
 * Switches on key in object, with default fallback
 * @param k - Key to look up
 * @param m - Object with values and optional default
 * @returns Value at key or default value
 */
export const switcher = (k, m) => m[k] === undefined ? m.default : m[k];
/** One minute in seconds */
export const MINUTE = 60;
/** One hour in seconds */
export const HOUR = 60 * MINUTE;
/** One day in seconds */
export const DAY = 24 * HOUR;
/** One week in seconds */
export const WEEK = 7 * DAY;
/** One month in seconds (approximate) */
export const MONTH = 30 * DAY;
/** One quarter in seconds (approximate) */
export const QUARTER = 90 * DAY;
/** One year in seconds (approximate) */
export const YEAR = 365 * DAY;
/**
 * Multiplies time unit by count
 * @param unit - Time unit in seconds
 * @param count - Number of units
 * @returns Total seconds
 */
export const int = (unit, count = 1) => unit * count;
/** Returns current Unix timestamp in seconds */
export const now = () => Math.round(Date.now() / 1000);
/**
 * Returns Unix timestamp from specified time ago
 * @param unit - Time unit in seconds
 * @param count - Number of units
 * @returns Timestamp in seconds
 */
export const ago = (unit, count = 1) => now() - int(unit, count);
/**
 * Converts seconds to milliseconds
 * @param seconds - Time in seconds
 * @returns Time in milliseconds
 */
export const ms = (seconds) => seconds * 1000;
/**
 * Fetches JSON from URL with options
 * @param url - URL to fetch from
 * @param opts - Fetch options
 * @returns Promise of parsed JSON response
 */
export const fetchJson = async (url, opts = {}) => {
    if (!opts.headers) {
        opts.headers = {};
    }
    if (!opts.headers["Accept"]) {
        opts.headers["Accept"] = "application/json";
    }
    const res = await fetch(url, opts);
    const json = await res.json();
    return json;
};
/**
 * Posts JSON data to URL
 * @param url - URL to post to
 * @param data - Data to send
 * @param opts - Additional fetch options
 * @returns Promise of parsed JSON response
 */
export const postJson = async (url, data, opts = {}) => {
    if (!opts.method) {
        opts.method = "POST";
    }
    if (!opts.headers) {
        opts.headers = {};
    }
    opts.headers["Content-Type"] = "application/json";
    opts.body = JSON.stringify(data);
    return fetchJson(url, opts);
};
/**
 * Uploads file to URL
 * @param url - Upload URL
 * @param file - File to upload
 * @returns Promise of parsed JSON response
 */
export const uploadFile = (url, file) => {
    const body = new FormData();
    body.append("file", file);
    return fetchJson(url, { method: "POST", body });
};
/**
 * Converts hex string to bech32 format
 * @param prefix - Bech32 prefix
 * @param hex - Hex string to convert
 * @returns Bech32 encoded string
 */
export const hexToBech32 = (prefix, hex) => bech32.encode(prefix, bech32.toWords(utf8.decode(hex)), false);
/**
 * Converts bech32 string to hex format
 * @param b32 - Bech32 string to convert
 * @returns Hex encoded string
 */
export const bech32ToHex = (b32) => utf8.encode(bech32.fromWords(bech32.decode(b32, false).words));
//# sourceMappingURL=Tools.js.map